import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';

import { formatAmountValue, pluralize, sqlDateToLabel, fractionToPercent } from 'helpers/helpers';
import H2 from 'apps/Pdf/components/common/H2';
import P from 'apps/Pdf/components/common/P';
import Spacer from 'apps/Pdf/components/common/Spacer';

import { Target, TargetFrequency, TargetType } from 'stores/workflowStores/finances/targets/model';
import useGeneralStyles from 'apps/Pdf/styles/general';
import useStyles from './TargetsStyle';
import { typeIcon } from 'pages/Accounts/Edit/components/iconHelper';
import Rent from './Rent';
import { RentData } from 'stores/workflowStores/finances/rent/model';
import { PersonType, Currency, EdwardColors } from 'enums';

interface Props {
  currency: Currency;
  data?: Target[];
  rentData?: RentData;
  personType: PersonType;
  annual?: boolean;
  actualInflation: number;
  hideInfo?: boolean;
  original?: boolean;
}

const Targets: React.FC<Props> = ({
  data,
  rentData,
  currency,
  personType,
  annual,
  actualInflation,
  hideInfo,
  original,
}) => {
  const { t } = useTranslation();
  const styles = useGeneralStyles();
  const classes = useStyles();

  return (data && data.length > 0) || rentData ? (
    <>
      <Spacer />
      <div className={styles.avoidPageBreak}>
        <H2>Nastavené cíle</H2>
        {!hideInfo && (
          <P>
            V investičním plánu je promítnuto nastavení cílů, které jsme si společně stanovili. Každý cíl znamená
            jednorázové nebo opakované čerpání hotovosti z portfolia. Vaše portfolio bude řízeno tak, aby byla na daný
            cíl včas připravena odpovídající částka v hotovosti. U každého cíle je uveden i zjednodušený údaj, s jakou
            pravděpodobností předpokládáme jeho splnění.
          </P>
        )}
        <Rent
          data={rentData}
          currency={currency}
          personType={personType}
          annual={annual}
          actualInflation={actualInflation}
          original={original}
        />
        {data &&
          data.length > 0 &&
          data.map((target) => {
            return (
              <Grid
                container
                item
                alignContent="center"
                alignItems="center"
                className={`${classes.target} ${target.type === TargetType.PORTFOLIO_RENT ? classes.rentTarget : ''}`}
                key={target.id}
              >
                <Grid item xs={1} className={classes.icon}>
                  {typeIcon(target.type, original || !target.enabled ? EdwardColors.GRAY_MEDIUM : undefined)}
                </Grid>
                <Grid item xs={7}>
                  <span className={styles.mediumText}>{target.title}</span>
                  <br />
                  {target.frequency === TargetFrequency.SINGLE ? (
                    sqlDateToLabel(target.since)
                  ) : (
                    <>
                      {t('general_since')}: {sqlDateToLabel(target.since)} {t('general_until')}:{' '}
                      {sqlDateToLabel(target.until)}
                    </>
                  )}
                  {target.inflation?.enabled && <> | plánovaná inflace: {fractionToPercent(target.inflation.rate)}</>}
                  {target.active && <strong> AKTIVNÍ</strong>}
                </Grid>
                <Grid item xs={2}>
                  <span className={styles.mediumText}>
                    {annual && target.inflation.valuation ? (
                      <></>
                    ) : (
                      <>{formatAmountValue(target.value.value, currency)}</>
                    )}
                  </span>
                  <br />
                  {target.frequency === TargetFrequency.REPEAT_EVERY ? (
                    <>
                      {' '}
                      {t('additionalTargets_every')} {target.repeatYears}{' '}
                      {pluralize(target.repeatYears, t('general_repeatYear'), t('general_repeatYears'))}
                    </>
                  ) : (
                    t(`general_frequency_${target.frequency}`)
                  )}{' '}
                </Grid>

                <Grid
                  item
                  xs={2}
                  className={`${styles.regularText} ${styles.center} ${classes.grade} ${
                    target.fulfillmentGrade ? 'grade_' + target.fulfillmentGrade : ''
                  }`}
                >
                  {target.fulfillmentGrade && (
                    <>
                      {t(`additionalTargets_fulfillmentGradePDF_${target.fulfillmentGrade}`)}
                      {target.fulfillment !== undefined && target.fulfillment > 0 && (
                        <>
                          <br />
                          na {fractionToPercent(target.fulfillment, 0)}
                        </>
                      )}
                    </>
                  )}
                  {!target.enabled && t('additionalTargets_inactive')}
                </Grid>

                {annual && target.inflation.valuation && (
                  <>
                    <Grid item xs={12} className={`${styles.bold} ${styles.regularText}`}>
                      Částka cíle se automaticky navyšuje podle{' '}
                      {target.inflation.type === 'ACTUAL' ? 'skutečné' : 'nastavené'} inflace{' '}
                      {fractionToPercent(target.inflation.type === 'PLANNED' ? target.inflation.rate : actualInflation)}{' '}
                      na <span className={styles.mediumText}>{formatAmountValue(target.value.value, currency)}</span>
                    </Grid>
                  </>
                )}
              </Grid>
            );
          })}
      </div>
    </>
  ) : null;
};

export default Targets;
